/**
 * Reads active VTTCues from a video element's text tracks and exposes
 * the cue content as reactive HTML for rendering in a custom overlay.
 *
 * This replaces native browser cue rendering (which relies on WebKit-only
 * pseudo-elements for positioning) with a cross-browser solution.
 *
 * Uses both cuechange and timeupdate for reliability:
 * - cuechange fires at cue boundaries (enter/exit)
 * - timeupdate polls active cues (catches mid-cue enable, post-VTT-load)
 */

import { ref, watch, onUnmounted, type Ref } from 'vue'

export function useSubtitleCues(
  videoRef: Ref<HTMLVideoElement | null>,
  subtitlesEnabled: Ref<boolean>
) {
  const activeCueHtml = ref('')

  let cleanupFns: (() => void)[] = []

  function getCueContent(cue: VTTCue): string {
    if (typeof cue.getCueAsHTML === 'function') {
      const fragment = cue.getCueAsHTML()
      const div = document.createElement('div')
      div.appendChild(fragment)
      // getCueAsHTML doesn't convert newlines to <br>
      return div.innerHTML.replace(/\n/g, '<br>')
    }
    // Fallback: strip VTT-specific tags, keep b/i/u
    return cue.text
      .replace(/<(?!\/?(?:b|i|u)\b)[^>]+>/gi, '')
      .replace(/\n/g, '<br>')
  }

  function readActiveCues(video: HTMLVideoElement) {
    if (!subtitlesEnabled.value) {
      activeCueHtml.value = ''
      return
    }

    const textTracks = video.textTracks
    for (let i = 0; i < textTracks.length; i++) {
      const track = textTracks[i]
      if (
        (track.kind === 'subtitles' || track.kind === 'captions') &&
        (track.mode === 'showing' || track.mode === 'hidden')
      ) {
        const cues = track.activeCues
        if (cues && cues.length > 0) {
          const parts: string[] = []
          for (let j = 0; j < cues.length; j++) {
            parts.push(getCueContent(cues[j] as VTTCue))
          }
          activeCueHtml.value = parts.join('<br>')
          return
        }
      }
    }
    activeCueHtml.value = ''
  }

  function setupListeners(video: HTMLVideoElement) {
    cleanup()

    const readCues = () => readActiveCues(video)

    // Listen on existing subtitle/caption tracks
    const textTracks = video.textTracks
    for (let i = 0; i < textTracks.length; i++) {
      const track = textTracks[i]
      if (track.kind === 'subtitles' || track.kind === 'captions') {
        track.addEventListener('cuechange', readCues)
        cleanupFns.push(() => track.removeEventListener('cuechange', readCues))
      }
    }

    // When HLS.js adds new tracks, listen to them too
    const onAddTrack = (e: TrackEvent) => {
      const track = e.track as TextTrack
      if (track && (track.kind === 'subtitles' || track.kind === 'captions')) {
        track.addEventListener('cuechange', readCues)
        cleanupFns.push(() => track.removeEventListener('cuechange', readCues))
      }
    }
    textTracks.addEventListener('addtrack', onAddTrack)
    cleanupFns.push(() => textTracks.removeEventListener('addtrack', onAddTrack))

    // timeupdate as fallback for mid-cue enable and post-VTT-load
    video.addEventListener('timeupdate', readCues)
    cleanupFns.push(() => video.removeEventListener('timeupdate', readCues))
  }

  function cleanup() {
    cleanupFns.forEach(fn => fn())
    cleanupFns = []
    activeCueHtml.value = ''
  }

  watch(videoRef, (video) => {
    if (video) {
      setupListeners(video)
    } else {
      cleanup()
    }
  }, { immediate: true })

  watch(subtitlesEnabled, () => {
    if (videoRef.value) {
      readActiveCues(videoRef.value)
    }
  })

  onUnmounted(cleanup)

  return { activeCueHtml }
}
